import {
	type EventGender,
	type EventQualifyingStandard,
	type MeGroup,
	type PersonGender,
	type ResultType,
	type ShortlistAthlete,
	type ShortlistAthleteRoleFilter,
	type ShortlistEntriesBody,
	type ShortlistEntryRefusal,
	type ShortlistEventView,
	type ShortlistRole,
	type ShortlistSportItem,
	type ShortlistSportView,
	formatMark,
	meetsStandard,
	parseMark,
} from "@seagames/contracts";

/**
 * The Shortlist pages' pure logic: the unsaved-changes buffer of the sport
 * page, the quota a pending change would leave, the counts of the strip and
 * the bottom bar, and the sports list's filter / sort. No React, no i18n —
 * unit-tested in model.test.ts.
 *
 * NONE of this decides anything: the batch PUT judges every row in its
 * writing statement (quota, the deadline, scope, eligibility) and answers per
 * row. The client only shows what a change would do before it is sent, and
 * keeps refused rows in the buffer with the API's reason.
 *
 * 0062: a row may also carry the athlete's best record for the event (on an
 * event with a qualifying standard). The tick / cross beside it is
 * contracts' `meetsStandard`, a display mirror; whether a PRIMARY without a
 * qualifying record is refused (`mark_required` / `below_standard`) is the
 * API's call, per row.
 */

/** One cell of the grid: an athlete's role in one event, or not entered. */
export type Cell = ShortlistRole | null;

/** `${ac_card_id}|${event_id}` — the batch PUT's own pair key. */
export const cellKey = (acCardId: string, eventId: string): string => `${acCardId}|${eventId}`;

export function splitKey(key: string): { ac_card_id: string; event_id: string } {
	const i = key.indexOf("|");
	return { ac_card_id: key.slice(0, i), event_id: key.slice(i + 1) };
}

/** A stored best record (0062): the mark in the canonical unit of the event's `result_type`. */
export interface BestRecord {
	mark: number | null;
	/** `YYYY-MM-DD`. */
	date: string | null;
	competition: string | null;
}

/** What the server holds: every (card, event) with a role, plus the pairs that hold an OVR entry. */
export interface ServerGrid {
	roles: ReadonlyMap<string, ShortlistRole>;
	entered: ReadonlySet<string>;
	/** Pairs with a stored best record (any of its three parts). */
	records: ReadonlyMap<string, BestRecord>;
	/** Pairs the API reads `meets_standard: false` (an administrator saved them anyway, or the standard moved). */
	belowStandard: ReadonlySet<string>;
	/** event_id to its qualifying standard; events without one are absent. */
	standards: ReadonlyMap<string, EventQualifyingStandard>;
}

export function serverGrid(view: Pick<ShortlistSportView, "athletes"> & Partial<Pick<ShortlistSportView, "events">>): ServerGrid {
	const roles = new Map<string, ShortlistRole>();
	const entered = new Set<string>();
	const records = new Map<string, BestRecord>();
	const belowStandard = new Set<string>();
	const standards = new Map<string, EventQualifyingStandard>();
	for (const e of view.events ?? []) if (e.qualifying) standards.set(e.event_id, e.qualifying);
	for (const a of view.athletes)
		for (const e of a.entries) {
			const k = cellKey(a.ac_card_id, e.event_id);
			roles.set(k, e.role);
			if (e.entered) entered.add(k);
			const mark = e.best_mark ?? null;
			const date = e.best_mark_date ?? null;
			const competition = e.best_mark_competition ?? null;
			// numeric(14,3) may arrive as a string from some drivers: read it as a number.
			if (mark !== null || date !== null || competition !== null) records.set(k, { mark: mark === null ? null : Number(mark), date, competition });
			if (e.meets_standard === false) belowStandard.add(k);
		}
	return { roles, entered, records, belowStandard, standards };
}

// -- Best records (0062) ------------------------------------------------------

/** A best record as typed: the mark in the sport's notation, the date (`YYYY-MM-DD` or ""), the competition. */
export interface RecordDraft {
	text: string;
	date: string;
	competition: string;
}

export const EMPTY_DRAFT: RecordDraft = { text: "", date: "", competition: "" };

/** A stored record as the inputs show it. */
export function draftOf(resultType: ResultType, record: BestRecord | undefined): RecordDraft {
	if (!record) return EMPTY_DRAFT;
	return { text: record.mark === null ? "" : formatMark(resultType, record.mark), date: record.date ?? "", competition: record.competition ?? "" };
}

/**
 * A draft as the record the PUT would send; null when its mark does not
 * parse. Text identical to the stored mark's printed form keeps the stored
 * value as is (touching only the date never re-reads the mark).
 */
export function draftToRecord(resultType: ResultType, draft: RecordDraft, stored?: BestRecord): BestRecord | null {
	const text = draft.text.trim();
	let mark: number | null = null;
	if (text !== "") {
		if (stored && stored.mark !== null && text === formatMark(resultType, stored.mark)) mark = stored.mark;
		else {
			const parsed = parseMark(resultType, text);
			if (!parsed.ok) return null;
			mark = parsed.value;
		}
	}
	const date = draft.date.trim();
	const competition = draft.competition.trim();
	return { mark, date: date === "" ? null : date, competition: competition === "" ? null : competition };
}

const sameRecord = (a: BestRecord, b: BestRecord | undefined): boolean =>
	(b?.mark ?? null) === a.mark && (b?.date ?? null) === a.date && (b?.competition ?? null) === a.competition;

/** The draft says what the server already holds (so it is no change). */
export function draftMatchesServer(server: ServerGrid, key: string, draft: RecordDraft): boolean {
	const std = server.standards.get(splitKey(key).event_id);
	if (!std) return false;
	const stored = server.records.get(key);
	const rec = draftToRecord(std.result_type, draft, stored);
	return rec !== null && sameRecord(rec, stored);
}

/** The record a row shows: the unsaved draft when there is one, else the server's. */
export function effectiveRecord(server: ServerGrid, pending: Pending, key: string): RecordDraft {
	const draft = pending.records.get(key);
	if (draft) return draft;
	const std = server.standards.get(splitKey(key).event_id);
	return std ? draftOf(std.result_type, server.records.get(key)) : EMPTY_DRAFT;
}

/**
 * The tick / cross beside a record: empty, unreadable, or (`meetsStandard`)
 * at or past the standard in the event's direction. Display only.
 */
export type RecordVerdict = "empty" | "invalid" | "meets" | "below";

export function recordVerdict(standard: EventQualifyingStandard, draft: RecordDraft): RecordVerdict {
	if (draft.text.trim() === "") return "empty";
	const parsed = parseMark(standard.result_type, draft.text);
	if (!parsed.ok) return "invalid";
	return meetsStandard(parsed.value, Number(standard.mark), standard.lower_is_better) ? "meets" : "below";
}

// ── The unsaved-changes buffer ──────────────────────────────────────────────

/**
 * Only the cells that DIFFER from the server, plus the API's refusal of a
 * cell that was sent and refused (kept until the user changes it again or
 * discards). Setting a cell back to what the server holds drops it.
 */
export interface Pending {
	changes: ReadonlyMap<string, Cell>;
	/** Best-record drafts that differ from the server (0062). */
	records: ReadonlyMap<string, RecordDraft>;
	refusals: ReadonlyMap<string, ShortlistEntryRefusal>;
}

export const EMPTY_PENDING: Pending = { changes: new Map(), records: new Map(), refusals: new Map() };

export type PendingAction =
	| { type: "set"; key: string; role: Cell; server: Cell }
	/** A best record typed for a row; a draft equal to the server's drops out. */
	| { type: "record"; key: string; draft: RecordDraft; server: ServerGrid }
	| { type: "discard" }
	/** After a save: the sent cells that were not refused are now the server's; refused ones stay with their reason. */
	| { type: "saved"; sent: readonly string[]; refused: readonly ShortlistEntryRefusal[] }
	/** After a refetch: drop changes the server now already holds (another tab, a colleague). */
	| { type: "rebase"; server: ServerGrid };

export function pendingReducer(state: Pending, action: PendingAction): Pending {
	switch (action.type) {
		case "set": {
			const changes = new Map(state.changes);
			const records = new Map(state.records);
			const refusals = new Map(state.refusals);
			refusals.delete(action.key);
			if (action.role === action.server) changes.delete(action.key);
			else changes.set(action.key, action.role);
			// A cleared row has no record to keep.
			if (action.role === null) records.delete(action.key);
			return { changes, records, refusals };
		}
		case "record": {
			const records = new Map(state.records);
			const refusals = new Map(state.refusals);
			refusals.delete(action.key);
			if (draftMatchesServer(action.server, action.key, action.draft)) records.delete(action.key);
			else records.set(action.key, action.draft);
			return { changes: state.changes, records, refusals };
		}
		case "discard":
			return EMPTY_PENDING;
		case "saved": {
			const refusedKeys = new Map(action.refused.map((r) => [cellKey(r.ac_card_id, r.event_id), r] as const));
			const changes = new Map(state.changes);
			const records = new Map(state.records);
			const refusals = new Map(state.refusals);
			for (const k of action.sent) {
				const refusal = refusedKeys.get(k);
				if (refusal) refusals.set(k, refusal);
				else {
					changes.delete(k);
					records.delete(k);
					refusals.delete(k);
				}
			}
			return { changes, records, refusals };
		}
		case "rebase": {
			let dirty = false;
			const changes = new Map(state.changes);
			const records = new Map(state.records);
			const refusals = new Map(state.refusals);
			for (const [k, role] of state.changes)
				if ((action.server.roles.get(k) ?? null) === role) {
					changes.delete(k);
					dirty = true;
				}
			for (const [k, draft] of state.records)
				// The server now holds it, or the event lost its standard (nothing to type a record against).
				if (!action.server.standards.has(splitKey(k).event_id) || draftMatchesServer(action.server, k, draft)) {
					records.delete(k);
					dirty = true;
				}
			for (const k of state.refusals.keys())
				if (!changes.has(k) && !records.has(k) && refusals.delete(k)) dirty = true;
			return dirty ? { changes, records, refusals } : state;
		}
	}
}

/** The role a cell shows: the unsaved change when there is one, else the server's. */
export function effectiveRole(server: ServerGrid, pending: Pending, key: string): Cell {
	return pending.changes.has(key) ? (pending.changes.get(key) ?? null) : (server.roles.get(key) ?? null);
}

/** Every row with an unsaved change: a role, a best record, or both. */
export function pendingKeys(pending: Pending): Set<string> {
	return new Set([...pending.changes.keys(), ...pending.records.keys()]);
}

/** Rows whose typed best record does not parse for the event's result type: Save waits for them. */
export function invalidRecordKeys(server: ServerGrid, pending: Pending): string[] {
	const out: string[] = [];
	for (const [k, draft] of pending.records) {
		const std = server.standards.get(splitKey(k).event_id);
		if (!std || effectiveRole(server, pending, k) === null) continue;
		if (draftToRecord(std.result_type, draft, server.records.get(k)) === null) out.push(k);
	}
	return out;
}

export type EntryRow = ShortlistEntriesBody["entries"][number];

/**
 * The batch PUT body rows for every unsaved change, in chunks of at most
 * `max`. A row carries its role (the unsaved one, else the server's) and,
 * when its record changed, the WHOLE record (the API replaces it whole). A
 * cleared row sends no record; a record that does not parse is not sent.
 */
export function pendingBatches(pending: Pending, server: ServerGrid, max: number): EntryRow[][] {
	const rows: EntryRow[] = [];
	for (const k of pendingKeys(pending)) {
		const role = effectiveRole(server, pending, k);
		const row: EntryRow = { ...splitKey(k), role };
		const draft = pending.records.get(k);
		const std = server.standards.get(row.event_id);
		const rec = draft && std && role !== null ? draftToRecord(std.result_type, draft, server.records.get(k)) : null;
		if (rec) {
			row.best_mark = rec.mark;
			row.best_mark_date = rec.date;
			row.best_mark_competition = rec.competition;
		}
		if (!pending.changes.has(k) && !rec) continue;
		rows.push(row);
	}
	const out: EntryRow[][] = [];
	for (let i = 0; i < rows.length; i += max) out.push(rows.slice(i, i + max));
	return out;
}

// ── Quota ──────────────────────────────────────────────────────────────────

export interface QuotaView {
	limit: number | null;
	primary: number;
	reserve: number;
	/** Places left for PRIMARY; null = no limit. Never below 0. */
	left: number | null;
	full: boolean;
	/** More PRIMARY than the limit (a host lowered it, or an admin edited). */
	over: boolean;
}

/**
 * An event's quota with the unsaved changes applied: the server's counts
 * (which cover every APPROVED card of the organisation) moved by the delta
 * of each pending cell of this event.
 */
export function quotaWithPending(event: Pick<ShortlistEventView, "event_id" | "quota">, server: ServerGrid, pending: Pending): QuotaView {
	let primary = event.quota.primary_used;
	let reserve = event.quota.reserve_used;
	for (const [k, role] of pending.changes) {
		const { event_id } = splitKey(k);
		if (event_id !== event.event_id) continue;
		const before = server.roles.get(k) ?? null;
		if (before === "PRIMARY") primary -= 1;
		if (before === "RESERVE") reserve -= 1;
		if (role === "PRIMARY") primary += 1;
		if (role === "RESERVE") reserve += 1;
	}
	primary = Math.max(0, primary);
	reserve = Math.max(0, reserve);
	const limit = event.quota.limit;
	return {
		limit,
		primary,
		reserve,
		left: limit === null ? null : Math.max(0, limit - primary),
		full: limit !== null && primary >= limit,
		over: limit !== null && primary > limit,
	};
}

/**
 * How far an event's PRIMARY places are filled: none yet, some, or the quota
 * reached (over counts as full; an event with no limit is never full). The
 * card's colour reads it from the pending-aware quota; the "Show" chips from
 * the SAVED quota, so a card does not vanish under the cursor while editing.
 */
export const EVENT_FILL_FILTERS = ["empty", "partial", "full"] as const;
export type EventFill = (typeof EVENT_FILL_FILTERS)[number];

export function eventFill(primary: number, limit: number | null): EventFill {
	if (primary <= 0) return "empty";
	return limit !== null && primary >= limit ? "full" : "partial";
}

// ── Counts (the strip and the bottom bar) ──────────────────────────────────

export interface SportCounts {
	athletes: number;
	/** Athletes with at least one PRIMARY. */
	primaryAthletes: number;
	primaryMale: number;
	primaryFemale: number;
	/** Events with at least one PRIMARY. */
	eventsEntered: number;
	events: number;
}

export function sportCounts(view: Pick<ShortlistSportView, "athletes" | "events">, server: ServerGrid, pending: Pending): SportCounts {
	let primaryAthletes = 0;
	let primaryMale = 0;
	let primaryFemale = 0;
	const enteredEvents = new Set<string>();
	for (const a of view.athletes) {
		let any = false;
		for (const e of view.events)
			if (effectiveRole(server, pending, cellKey(a.ac_card_id, e.event_id)) === "PRIMARY") {
				any = true;
				enteredEvents.add(e.event_id);
			}
		if (any) {
			primaryAthletes += 1;
			if (a.gender === "MALE") primaryMale += 1;
			if (a.gender === "FEMALE") primaryFemale += 1;
		}
	}
	return {
		athletes: view.athletes.length,
		primaryAthletes,
		primaryMale,
		primaryFemale,
		eventsEntered: enteredEvents.size,
		events: view.events.length,
	};
}

/** An athlete's cells that hold a role (pending applied), in the events' order. */
export function athleteRoles(
	athlete: Pick<ShortlistAthlete, "ac_card_id">,
	events: readonly Pick<ShortlistEventView, "event_id">[],
	server: ServerGrid,
	pending: Pending,
): { event_id: string; role: ShortlistRole }[] {
	const out: { event_id: string; role: ShortlistRole }[] = [];
	for (const e of events) {
		const role = effectiveRole(server, pending, cellKey(athlete.ac_card_id, e.event_id));
		if (role) out.push({ event_id: e.event_id, role });
	}
	return out;
}

/**
 * Who an event's "Add athlete" picker offers first: a men's event the men
 * (and anyone without a recorded gender), a women's event the women; mixed
 * and open events everyone. A convenience only — the API does not judge
 * gender.
 */
export function genderFits(eventGender: EventGender, gender: PersonGender | null): boolean {
	if (gender === null) return true;
	if (eventGender === "MEN") return gender === "MALE";
	if (eventGender === "WOMEN") return gender === "FEMALE";
	return true;
}

// ── By athlete: filter ─────────────────────────────────────────────────────

/** The By athlete chips: athletes with a PRIMARY somewhere, with only RESERVE roles, or with none. */
export { SHORTLIST_ATHLETE_ROLE_FILTERS as ATHLETE_ROLE_FILTERS } from "@seagames/contracts";
export type AthleteRoleFilter = ShortlistAthleteRoleFilter;

export function athleteRoleBucket(roles: readonly { role: ShortlistRole }[]): AthleteRoleFilter {
	if (roles.some((r) => r.role === "PRIMARY")) return "primary";
	if (roles.length > 0) return "reserve";
	return "none";
}

/** Case- and accent-blind match on the printed name or the card number (as printed or bare digits). */
/** The card number the GMS shows: ACC- once approved (every athlete here is), else REG-. */
export const cardNo = (a: Pick<ShortlistAthlete, "accreditation_no_display" | "registration_no_display">): string =>
	a.accreditation_no_display ?? a.registration_no_display;

export function athleteMatches(
	a: Pick<ShortlistAthlete, "printed_name" | "registration_no" | "registration_no_display" | "accreditation_no_display">,
	q: string,
): boolean {
	const needle = fold(q.trim());
	if (needle === "") return true;
	if (fold(a.printed_name).includes(needle)) return true;
	if (fold(a.registration_no_display).includes(needle)) return true;
	if (a.accreditation_no_display && fold(a.accreditation_no_display).includes(needle)) return true;
	const digits = needle.replace(/\D/g, "");
	return digits !== "" && String(a.registration_no).includes(String(Number(digits)));
}

const fold = (s: string): string =>
	s
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.toLowerCase();

// ── Which organisation the list may ask for without a pick ─────────────────

/**
 * The one organisation a scoped login's group covers for these games, when
 * its scope rows name exactly one (and no row means "any organisation") —
 * the same question the API asks before it answers 422 "choose an
 * organisation". Lets the list skip a request that can only be refused.
 * A hint for which request to send, never a permission: the API scopes
 * every row.
 */
export function singleScopeOrganization(group: Pick<MeGroup, "scopes"> | null | undefined, gamesId: string): string | null {
	if (!group) return null;
	const ids = new Set<string>();
	for (const s of group.scopes) {
		if (s.games && s.games.games_id !== gamesId) continue;
		if (!s.responsible_organization) return null;
		ids.add(s.responsible_organization.id);
	}
	return ids.size === 1 ? ([...ids][0] ?? null) : null;
}

// ── The sports list: filter / sort ─────────────────────────────────────────

export const SPORT_SORTS = ["athletes", "primary", "left", "name"] as const;
export type SportSort = (typeof SPORT_SORTS)[number];

export function filterSports(
	items: readonly ShortlistSportItem[],
	f: { q?: string; sort?: SportSort },
	compare: (a: string, b: string) => number,
): ShortlistSportItem[] {
	const needle = f.q ? fold(f.q.trim()) : "";
	const out = items.filter((s) => {
		if (needle && !fold(s.sport.name).includes(needle) && !fold(s.sport.code).includes(needle)) return false;
		return true;
	});
	const byName = (a: ShortlistSportItem, b: ShortlistSportItem) => compare(a.sport.name, b.sport.name);
	const sort = f.sort ?? "athletes";
	out.sort((a, b) => {
		switch (sort) {
			case "athletes":
				return b.athlete_count - a.athlete_count || byName(a, b);
			case "primary":
				return b.primary_count - a.primary_count || byName(a, b);
			case "left":
				// Sports with athletes first; within them, the fewest picks first.
				return Number(a.athlete_count === 0) - Number(b.athlete_count === 0) || a.primary_count - b.primary_count || byName(a, b);
			default:
				return byName(a, b);
		}
	});
	return out;
}

export interface SportsTotals {
	sports: number;
	athletes: number;
	primary: number;
	reserve: number;
}

export function sportsTotals(items: readonly ShortlistSportItem[]): SportsTotals {
	return items.reduce<SportsTotals>(
		(t, s) => ({
			sports: t.sports + 1,
			athletes: t.athletes + s.athlete_count,
			primary: t.primary + s.primary_count,
			reserve: t.reserve + s.reserve_count,
		}),
		{ sports: 0, athletes: 0, primary: 0, reserve: 0 },
	);
}
